import React, {
  ComponentProps,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from "react";
import {Tooltip as UiTooltip} from "@reside/ui";

/** Touch-primary devices synthesize mouseenter on tap; skip hover tooltips there. */
const HOVER_TOOLTIP_MEDIA = "(hover: hover) and (pointer: fine)";

const readPrefersHoverTooltip = () => {
  if (typeof window.matchMedia !== "function") {
    // jsdom and some SSR paths lack matchMedia; assume hover-capable (desktop/tests).
    return true;
  }

  return window.matchMedia(HOVER_TOOLTIP_MEDIA).matches;
};

type UiTooltipProps = ComponentProps<typeof UiTooltip>;

type Props = Readonly<
  HTMLAttributes<HTMLSpanElement> &
    Pick<UiTooltipProps, "content" | "isOpen" | "placement"> & {
      children: ReactNode;
      /** When true, hover/focus never opens the tooltip (e.g. disabled checklist labels). */
      disabled?: boolean;
    }
>;

/**
 * Tooltip that closes when the user scrolls any ancestor (or the window).
 * The UI kit tooltip only tracks hover and stays open during scroll.
 */
export const Tooltip = forwardRef<HTMLSpanElement, Props>(function Tooltip(
  {
    children,
    content,
    disabled = false,
    isOpen: controlledIsOpen,
    placement,
    className,
    style,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    onPointerDown,
    ...rest
  },
  ref,
) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [prefersHoverTooltip, setPrefersHoverTooltip] = useState(
    readPrefersHoverTooltip,
  );
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = disabled ? false : isControlled ? controlledIsOpen : hoverOpen;

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const media = window.matchMedia(HOVER_TOOLTIP_MEDIA);
    const onChange = () => setPrefersHoverTooltip(media.matches);

    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!isOpen || isControlled) {
      return undefined;
    }

    const dismiss = () => setHoverOpen(false);

    document.addEventListener("scroll", dismiss, true);
    window.addEventListener("touchmove", dismiss, {passive: true});

    return () => {
      document.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("touchmove", dismiss);
    };
  }, [isOpen, isControlled]);

  if (isControlled) {
    return (
      <UiTooltip
        isOpen={disabled ? false : controlledIsOpen}
        content={content}
        placement={placement}
      >
        {children}
      </UiTooltip>
    );
  }

  return (
    <UiTooltip isOpen={isOpen} content={content} placement={placement}>
      <span
        ref={ref}
        className={className}
        style={{display: "inline-flex", ...style}}
        onMouseEnter={event => {
          if (!disabled && prefersHoverTooltip) {
            setHoverOpen(true);
          }
          onMouseEnter?.(event);
        }}
        onMouseLeave={event => {
          setHoverOpen(false);
          onMouseLeave?.(event);
        }}
        onPointerDown={event => {
          setHoverOpen(false);
          onPointerDown?.(event);
        }}
        onFocus={event => {
          if (
            !disabled &&
            event.target instanceof Element &&
            event.target.matches(":focus-visible")
          ) {
            setHoverOpen(true);
          }
          onFocus?.(event);
        }}
        onBlur={event => {
          setHoverOpen(false);
          onBlur?.(event);
        }}
        {...rest}
      >
        {children}
      </span>
    </UiTooltip>
  );
});
